import { useEffect, useRef, useState } from "react";
import { Undo2, X, Search, Printer, Minus, Plus, WifiOff } from "lucide-react";
import { api, fmtGYD, apiErr } from "@/lib/api";
import { idb } from "@/lib/idb";
import { enqueue, runSync } from "@/lib/offline";
import { DEVICE_ID, printReceipt } from "@/lib/device";
import { Code128 } from "@/components/Barcode";

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const REASONS = ["Defective / damaged", "Wrong item", "Changed mind", "Expired", "Other"];
const REFUND_METHODS = [
  { key: "cash", label: "Cash refund" }, { key: "card", label: "Card reversal" },
  { key: "mobile_money", label: "Mobile Money (MMG)" }, { key: "store_credit", label: "Store credit" },
];
const LABEL = Object.fromEntries(REFUND_METHODS.map((m) => [m.key, m.label]));

async function findLocalSale(code) {
  const c = code.toUpperCase();
  const local = (await idb.all("sales")) || [];
  return local.find((s) => (s.receipt_no || "").toUpperCase() === c || s.idempotency_key.toUpperCase().startsWith(c));
}

export function ReturnModal({ ctx, onClose, onRefunded, initialCode = "" }) {
  const { shift, register, store, merchant, user } = ctx;
  const [code, setCode] = useState(initialCode);
  const [sale, setSale] = useState(null);
  const [qty, setQty] = useState({});
  const [reason, setReason] = useState(REASONS[0]);
  const [method, setMethod] = useState("cash");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const inputRef = useRef(null);
  const lookupRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); if (initialCode) lookupRef.current?.(); }, [initialCode]);

  const lookup = async () => {
    const c = code.trim();
    if (!c) return;
    setErr(""); setBusy(true);
    try {
      let s;
      try { const { data } = await api.get("/sales/lookup", { params: { receipt_no: c } }); s = data; await idb.put("sales", { ...data, id: data.id }); }
      catch (e) {
        if (e.response?.status === 404) throw e;
        s = await findLocalSale(c);
        if (!s) throw new Error("Offline — receipt not found on this register");
        s = { ...s, items: s.items.map((it) => ({ ...it, returned_quantity: 0, returnable_quantity: it.quantity })), offline_lookup: true };
      }
      setSale(s); setQty({});
      const first = s.payments?.[0]?.method;
      setMethod(first === "cash" || !first ? "cash" : first === "bank_transfer" ? "store_credit" : first);
    } catch (e) { setErr(apiErr(e, "Receipt not found")); }
    finally { setBusy(false); }
  };
  lookupRef.current = lookup;

  const setQ = (pid, v, max) => setQty((q) => ({ ...q, [pid]: Math.max(0, Math.min(max, v)) }));
  const lines = sale ? sale.items.filter((it) => (qty[it.product_id] || 0) > 0).map((it) => ({ product_id: it.product_id, name: it.name, quantity: qty[it.product_id], unit_price: it.unit_price, discount: 0, tax_rate: it.tax_rate, tax_exempt: it.tax_exempt })) : [];
  const refundTotal = +lines.reduce((s, l) => s + l.quantity * l.unit_price, 0).toFixed(2);

  const confirm = async () => {
    if (!lines.length) return;
    setBusy(true); setErr("");
    const idempotency_key = uuid();
    const payload = {
      id: idempotency_key, idempotency_key, sale_id: sale.id, items: lines, refund_method: method, reason,
      register_id: register?.id, shift_id: shift?.id, device_id: DEVICE_ID, created_offline: !navigator.onLine,
    };
    try {
      await enqueue({ id: idempotency_key, entity: "return", idempotency_key, payload });
      onRefunded(lines);
      setDone({ ...payload, refund_total: refundTotal, receipt_no: sale.receipt_no, cashier: user.name, at: new Date() });
      runSync(DEVICE_ID);
    } catch (e) { setErr(apiErr(e)); } finally { setBusy(false); }
  };

  if (done) return <RefundReceipt r={done} merchant={merchant} store={store} register={register} onClose={onClose} />;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="ms-card p-6 w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="return-modal">
        <div className="flex justify-between items-center mb-3"><h3 className="font-bold text-lg flex items-center gap-2"><Undo2 size={18} className="text-sky-300" /> Return / Refund</h3><button data-testid="return-close" onClick={onClose}><X size={20} /></button></div>
        <div className="flex gap-2 mb-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-2.5 text-slate-500" size={16} />
            <input ref={inputRef} data-testid="return-receipt-input" value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookup()}
              placeholder="Scan or type receipt number (e.g. R1-20260908-A1B2C3)" className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-9 pr-3 py-2.5 text-sm mono outline-none focus:border-sky-500" />
          </div>
          <button data-testid="return-lookup" onClick={lookup} disabled={busy || !code.trim()} className="touch-btn bg-sky-700 hover:bg-sky-600 rounded-lg px-4 text-sm disabled:opacity-50">{busy && !sale ? "…" : "Find"}</button>
        </div>
        {err && <div data-testid="return-error" className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2 mb-3">{err}</div>}

        {sale && (<>
          <div className="ms-card rounded-lg p-3 mb-3 text-xs text-slate-300 flex justify-between" data-testid="return-sale-summary">
            <div><div className="mono font-semibold text-sm text-white">{sale.receipt_no}</div><div>{new Date(sale.created_at || sale.local_created_at).toLocaleString()} · {sale.cashier_name}</div></div>
            <div className="text-right"><div className="mono font-semibold text-sm text-white">{fmtGYD(sale.total)}</div><div>{(sale.payments || []).map((p) => p.method).join(" + ")}</div></div>
          </div>
          {sale.offline_lookup && <div className="text-xs text-amber-300 flex items-center gap-1 mb-2"><WifiOff size={12} /> Offline — using this register's local copy; server will verify on sync.</div>}
          <div className="space-y-2 mb-3">
            {sale.items.map((it) => {
              const max = it.returnable_quantity ?? it.quantity;
              const q = qty[it.product_id] || 0;
              return (
                <div key={it.product_id} data-testid={`return-item-${it.product_id}`} className={`ms-card rounded-lg p-2.5 flex items-center gap-2 ${max === 0 ? "opacity-50" : ""}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{it.name}</div>
                    <div className="text-xs text-slate-400 mono">{fmtGYD(it.unit_price)} · sold {it.quantity}{it.returned_quantity ? ` · returned ${it.returned_quantity}` : ""}</div>
                  </div>
                  {max === 0 ? <span className="text-xs text-slate-500">Fully returned</span> : (
                    <div className="flex items-center gap-1">
                      <button data-testid={`return-minus-${it.product_id}`} onClick={() => setQ(it.product_id, q - 1, max)} className="touch-btn w-8 h-8 rounded-md bg-slate-800 flex items-center justify-center"><Minus size={14} /></button>
                      <span className="w-7 text-center mono" data-testid={`return-qty-${it.product_id}`}>{q}</span>
                      <button data-testid={`return-plus-${it.product_id}`} onClick={() => setQ(it.product_id, q + 1, max)} className="touch-btn w-8 h-8 rounded-md bg-slate-800 flex items-center justify-center"><Plus size={14} /></button>
                      <button data-testid={`return-all-${it.product_id}`} onClick={() => setQ(it.product_id, max, max)} className="touch-btn text-[11px] px-2 h-8 rounded-md bg-slate-800">All</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div><label className="text-xs text-slate-400 block mb-1">Reason</label>
              <select data-testid="return-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm">{REASONS.map((r) => <option key={r}>{r}</option>)}</select></div>
            <div><label className="text-xs text-slate-400 block mb-1">Refund method</label>
              <select data-testid="return-method" value={method} onChange={(e) => setMethod(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm">{REFUND_METHODS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}</select></div>
          </div>
          {method === "cash" && refundTotal > 0 && <div className="text-xs text-amber-300 mb-2">Cash refund will be deducted from this register's expected drawer cash on the Z-report.</div>}
          <div className="flex justify-between items-center text-lg font-bold mb-3"><span>Refund total</span><span className="mono text-sky-300" data-testid="return-total">{fmtGYD(refundTotal)}</span></div>
          <button data-testid="return-confirm" disabled={busy || !lines.length} onClick={confirm} className="w-full touch-btn bg-sky-600 hover:bg-sky-500 rounded-lg py-3 font-bold disabled:opacity-50 flex items-center justify-center gap-2"><Undo2 size={18} /> Refund {fmtGYD(refundTotal)} & restock</button>
        </>)}
      </div>
    </div>
  );
}

function RefundReceipt({ r, merchant, store, register, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 print:bg-white print:block" onClick={onClose}>
      <div id="receipt" data-testid="refund-receipt" className="bg-white text-black rounded-lg w-full max-w-[320px] p-5 print:max-w-full mono text-xs" onClick={(e) => e.stopPropagation()}>
        <div className="text-center mb-2"><div className="font-bold text-sm">{merchant?.name}</div><div>{store?.address}</div><div className="font-bold text-sm mt-1">REFUND RECEIPT</div></div>
        <div className="border-t border-dashed border-black my-1" />
        <div>Original receipt: {r.receipt_no}</div>
        <div>{r.at.toLocaleString()}</div>
        <div>Cashier: {r.cashier} · {register?.register_number}</div>
        <div>Reason: {r.reason}</div>
        {r.created_offline && <div className="font-bold">** OFFLINE — PENDING SYNC **</div>}
        <div className="border-t border-dashed border-black my-1" />
        {r.items.map((it, i) => <div key={i} className="flex justify-between"><span>{it.quantity}× {it.name}</span><span>-{(it.unit_price * it.quantity).toFixed(2)}</span></div>)}
        <div className="border-t border-dashed border-black my-1" />
        <div className="flex justify-between font-bold text-sm"><span>REFUND ({LABEL[r.refund_method]})</span><span data-testid="refund-receipt-total">-{r.refund_total.toFixed(2)}</span></div>
        <div className="mt-3 text-center">
          <Code128 value={r.receipt_no} height={36} testId="refund-receipt-barcode" />
          <div className="text-[10px] tracking-widest mt-0.5">{r.receipt_no}</div>
        </div>
        <div className="text-center mt-2">Items returned to stock. Thank you.</div>
        <div className="text-center text-[9px] mt-1">Ref: {r.idempotency_key.slice(0, 8)}</div>
      </div>
      <div className="absolute top-4 right-4 flex gap-2 print:hidden">
        <button data-testid="refund-print" onClick={printReceipt} className="bg-indigo-600 rounded-lg px-4 py-2 text-sm flex items-center gap-2 text-white"><Printer size={16} /> Print</button>
        <button data-testid="refund-close" onClick={onClose} className="bg-slate-700 rounded-lg px-4 py-2 text-sm text-white">Done</button>
      </div>
    </div>
  );
}
